import React, { useState, useEffect } from "react";
import { Modal, Button, Table, Form, Alert } from "react-bootstrap";
import "./CreateInvoice.css";
import { updateInvoice, updateClaim, createClaim } from "../../afinanceapi";

// ---------- HELPER FUNCTIONS (no locale) ----------

// Safely turn any value into a number, tolerant of commas, spaces, etc.
function normalizeToNumber(value) {
  if (typeof value === "number") return value;
  if (value == null) return 0;

  const str = String(value)
    .replace(/\s/g, "") // remove spaces
    .replace(/,/g, ".") // comma → dot
    .replace(/[^0-9.-]/g, ""); // keep digits, dot, minus

  const num = parseFloat(str);
  return Number.isNaN(num) ? 0 : num;
}

// Always return a string with "." as decimal separator, 2 decimals
function formatAmount(value) {
  const num = normalizeToNumber(value);
  return num.toFixed(2); // JS toFixed always uses "."
}

export default function ChangeLiabilityModal({
  show,
  onHide,
  lines = [],
  onSave,
  invoiceId,
  claimId,
  invoice,
  claim,
  account,
  practice,
  patient,
  userId,
  // When provided, a successful save calls this instead of reloading the
  // whole page, so an embedding screen (the ERA matcher) can refetch its own
  // data in place. PostedInvoice omits it and keeps the full reload.
  onSaved,
}) {
  const [lineValues, setLineValues] = useState([]);
  const [newClaimStatus, setNewClaimStatus] = useState("");

  useEffect(() => {
    if (Array.isArray(lines)) {
      const init = lines.map((line) => ({
        ...line,
        patientAmount: formatAmount(line.patientAmount || 0),
        medAidAmount: formatAmount(line.medAidAmount || 0),
      }));
      setLineValues(init);
    }
  }, [lines]);

  const handleAmountChange = (index, field, rawValue) => {
    const updated = [...lineValues];

    // 1) Strip unwanted chars, normalise comma→dot but keep as raw while typing
    const clean = rawValue
      .replace(/\s/g, "")
      .replace(/,/g, ".")
      .replace(/[^0-9.]/g, "");

    updated[index][field] = clean;

    // 2) Recalculate counterpart, keep it as raw (not formatted yet)
    const rate = normalizeToNumber(updated[index].rate);
    const num = normalizeToNumber(clean);

    const otherField =
      field === "patientAmount" ? "medAidAmount" : "patientAmount";
    const counterpart = Math.max(rate - num, 0);

    updated[index][otherField] = counterpart.toString();

    setLineValues(updated);
  };

  const handleBlur = (index, field) => {
    const updated = [...lineValues];

    const raw = String(updated[index][field] || "");
    const num = normalizeToNumber(raw);

    // 1) Format current field
    updated[index][field] = formatAmount(num);

    // 2) Recalculate & format counterpart
    const rate = normalizeToNumber(updated[index].rate);
    const otherField =
      field === "patientAmount" ? "medAidAmount" : "patientAmount";
    const counterpart = Math.max(rate - num, 0);

    updated[index][otherField] = formatAmount(counterpart);

    setLineValues(updated);
  };

  const fmt = (val) => formatAmount(val);

  // Validation: each line's patient + medical-aid split must not exceed its
  // rate (and neither field may be negative). Without this, a typo into a
  // single field can persist an inflated patientTotal/medAidTotal: the same
  // data-corruption pattern we traced to production invoices where
  // patientTotal stored a value larger than totalAmount, throwing aging and
  // statement balances out of sync.
  const lineError = (line) => {
    const rate = normalizeToNumber(line.rate);
    const pat = normalizeToNumber(line.patientAmount);
    const med = normalizeToNumber(line.medAidAmount);
    if (pat < 0 || med < 0) return "Amounts cannot be negative.";
    // Tolerance of 0.005 absorbs cent-level rounding from formatAmount.
    if (pat + med > rate + 0.005) {
      return `Patient + Medical Aid (R ${(pat + med).toFixed(
        2
      )}) exceeds line total (R ${rate.toFixed(2)}).`;
    }
    return null;
  };

  const lineErrors = lineValues.map(lineError);
  const hasAnyLineError = lineErrors.some((e) => e);

  const handleSave = async () => {
    // Belt-and-braces guard. The Save button is disabled when any line is
    // invalid, but this also protects against a race (state update between
    // re-render and click) or a programmatic invocation bypassing the UI.
    const errorsAtSave = lineValues.map(lineError);
    if (errorsAtSave.some((e) => e)) {
      return;
    }

    if (onSave) onSave(lineValues);

    // --- 1️⃣ Recalculate new invoice totals ---
    const patientTotal = lineValues.reduce(
      (acc, l) => acc + normalizeToNumber(l.patientAmount),
      0
    );
    const medAidTotal = lineValues.reduce(
      (acc, l) => acc + normalizeToNumber(l.medAidAmount),
      0
    );

    // Clear privatePayment on lines that now carry medical-aid liability:
    // otherwise downstream allocation (PostedInvoice line-split) still treats
    // them as patient-only and silently reverts the split on the next update.
    const linesToSave = lineValues.map((line) => {
      const medAid = normalizeToNumber(line.medAidAmount);
      if (line.privatePayment && medAid > 0) {
        return { ...line, privatePayment: false };
      }
      return line;
    });

    // When a plain "Posted" invoice ends up with any medical-aid liability,
    // it becomes a claimable medical-aid invoice: reflect that in the status
    // so the rest of the workflow picks it up. We deliberately don't gate on
    // the prior privatePayment flag: historical pre-scheme invoices were
    // never flagged private, so checking the flag would skip the exact
    // historical-private case this action is meant to handle.
    const wasJustPosted = String(invoice?.status || "").trim() === "Posted";
    const hasMedAidLiability = lineValues.some(
      (l) => normalizeToNumber(l.medAidAmount) > 0
    );
    const statusUpdate =
      wasJustPosted && hasMedAidLiability
        ? { status: "Posted - Claim in Progress" }
        : {};

    // Stamp invoice.medicalAid from the account's current scheme when the
    // invoice doesn't have one yet. Historical pre-scheme invoices store no
    // medicalAid block, and PostedInvoice's line-split (hasScheme check at
    // PostedInvoice.jsx:1100 / :1705) reads from invoice.medicalAid?.name, so
    // without this stamp, the next reload would re-classify every line as
    // patient liability and silently undo the shift we just made.
    const medicalAidUpdate =
      !invoice?.medicalAid?.name && account?.accountMedicalAid
        ? {
            medicalAid: {
              name: account.accountMedicalAid,
              plan: account.accountMedicalAidPlan || "",
              option: account.accountMedicalAidOption || "",
              number: account.accountMedicalAidNumber || "",
              optionCode: account.accountMedicalAidOptionCode || "",
            },
          }
        : {};

    // --- 2️⃣ Build updated invoice object ---
    const patientTotalRounded = Number(patientTotal.toFixed(2));
    const medAidTotalRounded = Number(medAidTotal.toFixed(2));

    // Deduct any existing receipts so outstanding is accurate
    const sumReceiptsFor = (bucket) =>
      (invoice.receipts || []).reduce((sum, r) => {
        const src = String(r.paymentSource || "").toLowerCase().replace(/\s+/g, "");
        const match = bucket === "patient" ? src === "patient" : src === "medicalaid";
        if (!match) return sum;
        const receiptAmt = normalizeToNumber(r.receiptAmount);
        const creditAmt = normalizeToNumber(r.creditNoteAmount);
        const refundAmt = normalizeToNumber(r.refundAmount);
        return Number((sum + receiptAmt + creditAmt - refundAmt).toFixed(2));
      }, 0);

    const patientReceipts = sumReceiptsFor("patient");
    const medAidReceipts = sumReceiptsFor("medAid");
    const patientOutstanding = Number((patientTotalRounded - patientReceipts).toFixed(2));
    const medAidOutstanding = Number((medAidTotalRounded - medAidReceipts).toFixed(2));
    const totalOutstanding = Number((patientOutstanding + medAidOutstanding).toFixed(2));
    const paidAmount = Number((patientReceipts + medAidReceipts).toFixed(2));

    const invoiceToSave = {
      ...invoice,
      lines: linesToSave,
      patientTotal: patientTotalRounded,
      totalPatientAmount: patientTotalRounded,
      medAidTotal: medAidTotalRounded,
      totalMedicalAidAmount: medAidTotalRounded,
      outstandingBalance: {
        ...(invoice.outstandingBalance || {}),
        patientOutstanding,
        medAidOutstanding,
        totalOutstanding,
        paidAmount,
      },
      ...medicalAidUpdate,
      ...statusUpdate,
    };

    // --- 3️⃣ Save Invoice ---
    await updateInvoice(invoiceId, invoiceToSave);

    // --- 3.5️⃣ Auto-create a Pending Submission claim when liability has
    //          shifted onto the medical aid on an invoice that didn't have a
    //          claim yet. Mirrors the minimal claim shape from
    //          PostedInvoice.handleManualClaim so the existing claim flows
    //          (E-Submission / Manual Submission / Update Claim) can pick it
    //          up on the next page load.
    if (wasJustPosted && hasMedAidLiability && !claimId) {
      try {
        await createClaim({
          status: "Pending Submission",
          accountId: account?._id,
          invoiceId: invoiceId,
          practiceId: practice?._id,
          patientId: patient?._id,
          createdBy: userId,
          createdDate: new Date().toISOString(),
          userId,
        });
      } catch (err) {
        // 409 = the invoice already has a live claim, which is the state this
        // block is trying to reach anyway. The invoice save above has already
        // committed, so don't fail the whole operation over it.
        if (err?.response?.status !== 409) throw err;
      }
    }

    // --- 4️⃣ If Claim Exists, Update Claim Financials ---
    if (claimId && claim) {
      const updatedClaim = { ...claim };

      // Stamp who did this and why into the claim's history entry: this
      // save overwrites ERA-stamped statuses, so an unlabelled entry here
      // makes later investigations needlessly hard.
      updatedClaim.action = "Liability Changed";
      updatedClaim.userId = userId || null;

      // Update claim status
      if (newClaimStatus) updatedClaim.status = newClaimStatus;

      // Update each claim line’s approved amounts
      updatedClaim.lineResults = updatedClaim.lineResults.map((cLine) => {
        const match = lineValues.find(
          (l) =>
            l.lineNumber === cLine.lineNumber ||
            l.tariffCode === cLine.tariffCode
        );
        if (match) {
          const medAid = formatAmount(match.medAidAmount || 0);
          return {
            ...cLine,
            approvedGrossAmount: medAid,
            approvedNettAmount: medAid,
          };
        }
        return cLine;
      });

      // Calculate new totals
      const totalApprovedNum = updatedClaim.lineResults.reduce(
        (acc, l) => acc + normalizeToNumber(l.approvedNettAmount),
        0
      );
      const totalApproved = formatAmount(totalApprovedNum);

      updatedClaim.financials = {
        ...updatedClaim.financials,
        approvedGross: totalApproved,
        approvedNett: totalApproved,
      };

      // Save claim
      await updateClaim(claimId, updatedClaim);
    }

    // --- 5️⃣ Refresh & Close ---
    if (onSaved) {
      await onSaved();
      onHide();
      return;
    }
    window.location.reload();
    onHide();
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      centered
      backdrop="static"
      dialogClassName="modal-75w"
    >
      <Modal.Header
        style={{ background: "#CFE2FF" }}
        className="rounded-0 text-primary d-flex align-items-center"
        closeButton
      >
        <Modal.Title as="h6" className="m-0">
          Change Liability
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {lineValues.length === 0 ? (
          <p className="text-muted text-center">No lines available.</p>
        ) : (
          <>
            <span className="text-primary mb-1">
              Update the Patient or Medical Aid Amounts as needed:
            </span>

            <Table bordered hover responsive size="sm" className="align-middle">
              <thead className="table-primary text-center">
                <tr>
                  <th
                    className="text-primary fw-semibold"
                    style={{ width: "10%" }}
                  >
                    Date
                  </th>
                  <th
                    className="text-primary fw-semibold"
                    style={{ width: "10%" }}
                  >
                    Tariff Code
                  </th>
                  <th
                    className="text-primary fw-semibold"
                    style={{ width: "50%" }}
                  >
                    Description
                  </th>
                  <th
                    className="text-primary fw-semibold"
                    style={{ width: "10%" }}
                  >
                    Total (R)
                  </th>
                  <th
                    className="text-primary fw-semibold"
                    style={{ width: "10%" }}
                  >
                    Patient (R)
                  </th>
                  <th
                    className="text-primary fw-semibold"
                    style={{ width: "10%" }}
                  >
                    Medical Aid (R)
                  </th>
                </tr>
              </thead>

              <tbody>
                {lineValues.map((line, idx) => {
                  const claimLine =
                    claim?.lineResults?.find(
                      (c) =>
                        c.lineNumber === line.lineNumber ||
                        c.tariffCode === line.tariffCode
                    ) || {};

                  const approvedRaw =
                    claimLine?.approvedNettAmount ||
                    claimLine?.approvedGrossAmount ||
                    "0.00";

                  const approved = normalizeToNumber(approvedRaw);
                  const isApprovedPositive = approved > 0;

                  const formattedDate =
                    line.appointmentDateCustom ||
                    line.appointmentDateSet ||
                    (claimLine?.dateOfService
                      ? `${claimLine.dateOfService.slice(
                          0,
                          4
                        )}-${claimLine.dateOfService.slice(
                          4,
                          6
                        )}-${claimLine.dateOfService.slice(6, 8)}`
                      : "-");

                  return (
                    <React.Fragment key={idx}>
                      {/* --- Approved Row --- */}
                      {claimLine && claimId && (
                        <tr
                          className={
                            isApprovedPositive
                              ? "approved-row-green"
                              : "approved-row-red"
                          }
                        >
                          <td
                            colSpan={5}
                            className="text-start ps-4 small border-top fw-semibold text-primary align-middle"
                          >
                            <i
                              className={`fa ${
                                isApprovedPositive
                                  ? "fa-check-circle text-success"
                                  : "fa-times-circle text-danger"
                              } me-2`}
                            ></i>
                            Approved Amount (from claim)
                          </td>
                          <td
                            className={`text-end me-2 fw-semibold small ${
                              isApprovedPositive
                                ? "text-success"
                                : "text-danger"
                            }`}
                          >
                            R {approved.toFixed(2)}
                          </td>
                        </tr>
                      )}

                      {/* --- Invoice Line Row --- */}
                      <tr>
                        <td className="text-center align-middle">
                          {formattedDate}
                        </td>
                        <td className="text-center align-middle">
                          {line.tariffCode || claimLine.tariffCode || "-"}
                        </td>
                        <td className="align-middle">
                          {line.description || claimLine.description || "-"}
                        </td>
                        <td className="text-end fw-semibold text-primary align-middle">
                          {fmt(line.rate)}
                        </td>
                        <td className="align-middle">
                          <Form.Control
                            type="text"
                            size="sm"
                            className="text-end flex-grow-1 border-0"
                            value={line.patientAmount}
                            isInvalid={!!lineErrors[idx]}
                            onChange={(e) =>
                              handleAmountChange(
                                idx,
                                "patientAmount",
                                e.target.value
                              )
                            }
                            onBlur={() => handleBlur(idx, "patientAmount")}
                          />
                        </td>
                        <td className="align-middle">
                          <Form.Control
                            type="text"
                            size="sm"
                            className="text-end flex-grow-1 border-0"
                            value={line.medAidAmount}
                            isInvalid={!!lineErrors[idx]}
                            onChange={(e) =>
                              handleAmountChange(
                                idx,
                                "medAidAmount",
                                e.target.value
                              )
                            }
                            onBlur={() => handleBlur(idx, "medAidAmount")}
                          />
                        </td>
                      </tr>
                      {lineErrors[idx] && (
                        <tr>
                          <td colSpan={6} className="text-danger small py-1">
                            <i className="fa fa-exclamation-circle me-2"></i>
                            {lineErrors[idx]}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </Table>

            {/* Advisory + Status Dropdown */}
            {claimId && (
              <Alert variant="warning" className="text-dark small mt-3">
                <p className="mb-2">
                  <strong>Note:</strong> The invoice has a claim associated with
                  it. Changing the liability will <strong>not</strong> alter the
                  original claim submitted to the Medical Aid.
                </p>
                <p className="mb-2">
                  Only use <em>Change Liability</em> after the Medical Aid has
                  processed the claim and an official outcome was received. If
                  you choose to go ahead, please select the appropriate new
                  status for the claim below:
                </p>
                <Form.Select
                  size="sm"
                  className="mt-2 w-auto"
                  value={newClaimStatus}
                  onChange={(e) => setNewClaimStatus(e.target.value)}
                >
                  <option value="">-- Select New Claim Status --</option>
                  <option value="Finalised">Finalised</option>
                  <option value="Processed">Processed</option>
                  <option value="Processed - Not Paid">
                    Processed - Not Paid
                  </option>
                  <option value="Processed - Claim Reversed">
                    Processed - Claim Reversed
                  </option>
                </Form.Select>
              </Alert>
            )}
          </>
        )}
      </Modal.Body>

      <Modal.Footer className="d-flex justify-content-between">
        <Button variant="outline-secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={(claimId && !newClaimStatus) || hasAnyLineError}
        >
          <i className="fa-solid fa-save me-2"></i>Save Changes
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
